import { DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";
import { Dialog, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { userIsReadOnly } from "../../auth/AuthService";
import DotLegalModal from "../../common/modal/DotLegalModal";
import downloadIcon from "../icons/Download.svg";
import { ApplicationUser } from "../users/User.types";
import { CaseStatus } from "../worldMap/WorldMap.types";
import { CaseType, Device } from "./Case.types";
import useCaseModalContentStyles from "./CaseModalContent.styles";
import { downloadDocument, formatDate, getCase, getDevice } from "./CaseService";

export interface CaseModalContentProps {
    caseId: number;
    country: string;
    caseType: CaseType;
    verifiedUser: ApplicationUser | null;
    onClose: () => void;
}

function CaseModalContentRow(props: { label: string; value: string | undefined }) {
    return (
        <Grid item container md spacing={1}>
            <Grid item sm={12}>
                <Typography variant="h5">{props.label}</Typography>
            </Grid>
            <Grid item sm={12}>
                {props.value}
            </Grid>
        </Grid>
    );
}

// TODO: HTML return needs some refactoring and split into more understandable components
function CaseModalContent(props: CaseModalContentProps) {
    const classes = useCaseModalContentStyles();
    const { data: selectedCase } = useQuery(["case", props.caseId, props.country], () => getCase(props.caseId, props.country));
    const [selectedDevice, setSelectedDevice] = useState<Device>();
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [canZoom, setCanZoom] = useState(false);
    const history = useHistory();

    const onCaseLinkClicked = (caseId: number, caseCountry: string, caseType: CaseType, caseStatus: CaseStatus, isDispute: boolean = false): void => {
        if (!selectedCase) return;
        const country = caseCountry === "International Protocol (Madrid)" ? selectedCase.country : caseCountry;
        if (isDispute) {
            history.push("/disputes", { caseId, country, caseStatus, types: caseType });
            return;
        }
        switch (caseType) {
            case CaseType.Trademark:
                history.push("/trademarks", { caseId, country, caseStatus });
                break;
            case CaseType.Design:
                history.push("/designs", { caseId, country, caseStatus });
                break;
            case CaseType.Copyright:
                history.push("/copyrights", { caseId, country, caseStatus });
                break;
            default:
                break;
        }
    };

    const onSelectedDeviceChanged = (deviceId: number, caseId: number) => {
        setCanZoom(false);
        getDevice(deviceId, caseId)
            .then((getDeviceRes) => setSelectedDevice(getDeviceRes))
            .catch(() => setSelectedDevice(undefined));
    };

    useEffect(() => {
        if (!selectedCase) return;
        const mainDevice = selectedCase.devices.find((device) => device.isMain);
        if (mainDevice) onSelectedDeviceChanged(mainDevice.id, selectedCase.id);
        // eslint-disable-next-line
    }, [selectedCase]);

    const body = !selectedCase ? (
        <DotLegalFullScreenSpinner />
    ) : (
        <Grid className={classes.root} container alignItems="center" spacing={6}>
            <Grid item container sm={12} spacing={1}>
                <CaseModalContentRow label={props.caseType === CaseType.Watches ? "Watch" : props.caseType} value={selectedCase.name} />
                <CaseModalContentRow label="Case no." value={selectedCase.caseNo} />
                <CaseModalContentRow label="Matter ID" value={selectedCase.matterId} />
                <CaseModalContentRow label="Country" value={selectedCase.country} />
                <CaseModalContentRow
                    label="Status"
                    value={
                        selectedCase.isDispute && selectedCase.status !== "Pending"
                            ? selectedCase.disputeDecision[0].toUpperCase() + selectedCase.disputeDecision.substr(1).toLowerCase()
                            : selectedCase.status
                    }
                />
            </Grid>
            {props.caseType === CaseType.Watches ? (
                <Grid item container sm={12} spacing={1}>
                    <CaseModalContentRow label={"Expiry date"} value={selectedCase.renewalDate && formatDate(selectedCase.renewalDate)} />
                    <CaseModalContentRow label="Applicant" value={selectedCase.applicant} />
                    <CaseModalContentRow label="Classes" value={selectedCase.classes?.map((tooltip) => tooltip.value).join(", ")} />
                    {!userIsReadOnly(props.verifiedUser) ? (
                        <CaseModalContentRow
                            label="Latest estimated renewal price"
                            value={
                                selectedCase.latestRenewalPrice
                                    ? `${selectedCase.latestRenewalPrice.toLocaleString("en", { maximumFractionDigits: 0 })} ${
                                          selectedCase.currencyCode ?? "DKK"
                                      } ${selectedCase.renewalPriceGivenAt ? `(${formatDate(selectedCase.renewalPriceGivenAt)})` : ``}`
                                    : ""
                            }
                        />
                    ) : (
                        <CaseModalContentRow label={""} value={""} />
                    )}
                    <CaseModalContentRow label={""} value={""} />
                </Grid>
            ) : (
                <>
                    <Grid item container sm={12} spacing={1}>
                        <CaseModalContentRow label="Application No." value={selectedCase.applicationNo} />
                        <CaseModalContentRow label="Application Date" value={selectedCase.applicationDate && formatDate(selectedCase.applicationDate)} />
                        <CaseModalContentRow label="Registration No." value={selectedCase.registrationNo} />
                        <CaseModalContentRow label="Registration Date" value={selectedCase.registrationDate && formatDate(selectedCase.registrationDate)} />
                        {!selectedCase.isDispute && selectedCase.status !== "Closed" ? (
                            <CaseModalContentRow
                                label={props.caseType === CaseType.Copyright ? "Expiry date" : "Renewal Date"}
                                value={selectedCase.renewalDate && formatDate(selectedCase.renewalDate)}
                            />
                        ) : (
                            <CaseModalContentRow label="Case creation date" value={selectedCase.dateOfOrder && formatDate(selectedCase.dateOfOrder)} />
                        )}
                    </Grid>
                    <Grid item container sm={12} spacing={1}>
                        {props.caseType === CaseType.Trademark && !selectedCase.isDispute && (
                            <CaseModalContentRow label="Use Date" value={selectedCase.useDate && formatDate(selectedCase.useDate)} />
                        )}
                        {!selectedCase.isDispute && <CaseModalContentRow label="Applicant" value={selectedCase.applicant} />}
                        {!selectedCase.isDispute && <CaseModalContentRow label="Correspondence" value={selectedCase.correspondence} />}
                        {props.caseType !== CaseType.Copyright && (
                            <CaseModalContentRow label="Classes" value={selectedCase.classes?.map((tooltip) => tooltip.value).join(", ")} />
                        )}
                        {props.caseType === CaseType.Design && (
                            <CaseModalContentRow label="End of duration" value={selectedCase.endOfDurationDate && formatDate(selectedCase.endOfDurationDate)} />
                        )}
                        {!selectedCase.isDispute && props.caseType !== CaseType.Copyright && (
                            <>
                                {!userIsReadOnly(props.verifiedUser) ? (
                                    <CaseModalContentRow
                                        label="Latest estimated renewal price"
                                        value={
                                            selectedCase.latestRenewalPrice
                                                ? `${selectedCase.latestRenewalPrice.toLocaleString("en", { maximumFractionDigits: 0 })} ${
                                                      selectedCase.currencyCode ?? "DKK"
                                                  } ${selectedCase.renewalPriceGivenAt ? `(${formatDate(selectedCase.renewalPriceGivenAt)})` : ``}`
                                                : ""
                                        }
                                    />
                                ) : (
                                    <CaseModalContentRow label="" value="" />
                                )}
                            </>
                        )}
                        {props.caseType === CaseType.Copyright && <CaseModalContentRow label="" value="" />}
                        {selectedCase.isDispute && <CaseModalContentRow label="Case type" value={selectedCase.type} />}
                        {selectedCase.isDispute && <CaseModalContentRow label="Dispute against" value={selectedCase.dispute} />}
                        {selectedCase.isDispute && (
                            <>
                                <CaseModalContentRow label="Counterparty" value={selectedCase.counterParty} />
                                <CaseModalContentRow label="Address" value={selectedCase.counterPartyAddress} />
                            </>
                        )}
                    </Grid>
                </>
            )}

            {selectedCase.remark && (
                <Grid item container sm={12} spacing={1}>
                    <Grid item sm={12}>
                        <Typography variant="h5">Remark</Typography>
                    </Grid>
                    <Grid item sm={12}>
                        {selectedCase.remark}
                    </Grid>
                </Grid>
            )}
            {(props.caseType === CaseType.Trademark || props.caseType === CaseType.Watches) && selectedCase.classes && selectedCase.classes.length > 0 && (
                <Grid item container sm={12} spacing={1}>
                    <>
                        <Grid item sm={12}>
                            <Typography variant="h5">Goods & Services</Typography>
                        </Grid>
                        {selectedCase.classes?.map((caseData) => (
                            <Grid item sm={12} style={{ fontSize: 12 }} key={caseData.value}>
                                <strong>{caseData.value}: </strong>
                                {caseData.tooltip}
                            </Grid>
                        ))}
                    </>
                </Grid>
            )}
            {selectedCase.disputeRelations && selectedCase.disputeRelations.length > 0 && (
                <Grid item container sm={12} spacing={1}>
                    <>
                        {selectedCase.isDispute ? (
                            <>
                                <Grid item sm={12}>
                                    <Typography variant="h5">Link to registration case</Typography>
                                </Grid>
                                {selectedCase.disputeRelations?.map((disputeRelation) => (
                                    <Grid
                                        item
                                        sm={12}
                                        className={classes.caseLink}
                                        key={disputeRelation.caseId}
                                        onClick={() =>
                                            onCaseLinkClicked(disputeRelation.caseId, disputeRelation.country, disputeRelation.type, disputeRelation.status)
                                        }
                                    >
                                        {disputeRelation.catchword}
                                    </Grid>
                                ))}
                            </>
                        ) : (
                            <>
                                {selectedCase.disputeRelations?.filter(
                                    (disputeRelation) => disputeRelation.status === "pending" || disputeRelation.status === "registered"
                                ).length > 0 && (
                                    <>
                                        <Grid item sm={12}>
                                            <Typography variant="h5">Link to pending disputes</Typography>
                                        </Grid>
                                        {selectedCase.disputeRelations
                                            ?.filter((disputeRelation) => disputeRelation.status === "pending" || disputeRelation.status === "registered")
                                            .map((disputeRelation) => (
                                                <Grid
                                                    item
                                                    sm={12}
                                                    className={classes.caseLink}
                                                    key={disputeRelation.caseId}
                                                    onClick={() =>
                                                        onCaseLinkClicked(
                                                            disputeRelation.caseId,
                                                            disputeRelation.country,
                                                            disputeRelation.type,
                                                            disputeRelation.status,
                                                            true
                                                        )
                                                    }
                                                >
                                                    {disputeRelation.catchword}
                                                </Grid>
                                            ))}
                                    </>
                                )}
                                {selectedCase.disputeRelations?.filter(
                                    (disputeRelation) => disputeRelation.status !== "pending" && disputeRelation.status !== "registered"
                                ).length > 0 && (
                                    <>
                                        <Grid item sm={12}>
                                            <Typography variant="h5">Link to past disputes</Typography>
                                        </Grid>
                                        {selectedCase.disputeRelations
                                            ?.filter((disputeRelation) => disputeRelation.status !== "pending" && disputeRelation.status !== "registered")
                                            .map((disputeRelation) => (
                                                <Grid
                                                    item
                                                    sm={12}
                                                    className={classes.caseLink}
                                                    key={disputeRelation.caseId}
                                                    onClick={() =>
                                                        onCaseLinkClicked(
                                                            disputeRelation.caseId,
                                                            disputeRelation.country,
                                                            disputeRelation.type,
                                                            disputeRelation.status,
                                                            true
                                                        )
                                                    }
                                                >
                                                    {disputeRelation.catchword}
                                                </Grid>
                                            ))}
                                    </>
                                )}
                            </>
                        )}
                    </>
                </Grid>
            )}
            {selectedCase.documents && selectedCase.documents.length > 0 && (
                <Grid item container sm={12} spacing={1}>
                    <Grid item sm={12}>
                        <Typography variant="h5">Documents</Typography>
                    </Grid>
                    {selectedCase.documents.map((document) => (
                        <Grid key={document.id} item sm={12}>
                            <span
                                onClick={() => downloadDocument(document.id, document.name)}
                                onKeyDown={() => downloadDocument(document.id, document.name)}
                                role="button"
                                tabIndex={0}
                                className={classes.caseLink}
                            >
                                <img src={downloadIcon} alt="download" style={{ marginRight: 10 }} />
                                {document.name}
                            </span>
                        </Grid>
                    ))}
                </Grid>
            )}
            {(selectedDevice || (selectedCase.devices && selectedCase.devices.length > 1)) && (
                <Grid item container sm={12} spacing={2}>
                    <Grid item sm={12}>
                        <Typography variant="h5">{props.caseType === CaseType.Trademark ? "Figurative mark" : "Image"}</Typography>
                    </Grid>
                    {selectedCase.devices && selectedCase.devices.length > 1 && (
                        <Grid item container sm={6} spacing={1} alignContent="flex-start">
                            {selectedCase.devices.map((device) => (
                                <Grid item sm={12} key={device.id}>
                                    <span
                                        onClick={() => onSelectedDeviceChanged(device.id, selectedCase.id)}
                                        onKeyDown={() => onSelectedDeviceChanged(device.id, selectedCase.id)}
                                        role="button"
                                        tabIndex={0}
                                        className={`${classes.caseLink} ${selectedDevice && selectedDevice.id === device.id ? classes.caseLinkSelected : null}`}
                                    >
                                        {device.name}
                                    </span>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    {selectedDevice && (
                        <Grid item container sm={6} justifyContent="flex-start" alignItems="flex-start">
                            <img
                                src={`data:image/jpeg;base64,${selectedDevice!.base64}`}
                                alt="Device"
                                onLoad={(e) => setCanZoom(e.currentTarget.naturalWidth > 600 || e.currentTarget.naturalHeight > 600)}
                                onClick={canZoom ? () => setIsZoomOpen(true) : undefined}
                                style={{ maxWidth: "100%", maxHeight: 600, height: "auto", cursor: canZoom ? "zoom-in" : "default" }}
                            />
                            <Dialog open={isZoomOpen} onClose={() => setIsZoomOpen(false)} maxWidth={false}>
                                <img
                                    src={`data:image/jpeg;base64,${selectedDevice!.base64}`}
                                    alt="Device"
                                    onClick={() => setIsZoomOpen(false)}
                                    style={{ maxWidth: "90vw", maxHeight: "80vh", height: "auto", cursor: "zoom-out", display: "block" }}
                                />
                            </Dialog>
                        </Grid>
                    )}
                </Grid>
            )}
        </Grid>
    );

    return <DotLegalModal content={body} maxWidth="md" onClose={props.onClose} open title={selectedCase?.name ?? ""} />;
}

export default CaseModalContent;
